import Button from 'components/Button';
import FormActions from 'components/Form/FormActions';
import SubmitButton from 'components/Form/SubmitButton';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';

import {usePostDelete} from '../services';
import {
    DeletionSubmissionFormData,
    EmployeeUserAccountToDelete,
    PartnersToBeDeleted,
} from '../types';

export type DeletionConfirmationProps = {
    formData: DeletionSubmissionFormData;
    onCancel: () => void;
    previewData: {
        allPartnersToDelete: Array<PartnersToBeDeleted>;
        employeeUserAccountsToDelete: Array<EmployeeUserAccountToDelete>;
        willDeleteUser: boolean;
    };
};

const DeletionConfirmation: FC<DeletionConfirmationProps> = ({
    formData,
    onCancel,
    previewData: {
        allPartnersToDelete,
        employeeUserAccountsToDelete,
        willDeleteUser,
    },
}) => {
    const methods = useForm({
        shouldUnregister: true,
    });
    const {handleSubmit} = methods;

    const {mutateAsync} = usePostDelete();

    const onSubmit = async () => {
        await mutateAsync(formData)
            .then(() => {
                setUrl(window.Urls.overview_view());
            })
            .catch(onApiFormError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex max-w-xl flex-col gap-2">
                    <p className="font-semibold">{t`The following data will be permanently deleted and cannot be recovered once deleted.`}</p>
                    {willDeleteUser && (
                        <div>
                            <p className="font-semibold text-light-secondary">{t`Your user account:`}</p>
                            <ul>
                                <li>{t`You will no longer be able to use MakeLeaps.`}</li>
                            </ul>
                        </div>
                    )}
                    {!!employeeUserAccountsToDelete?.length && (
                        <div>
                            <p className="font-semibold text-light-secondary">{t`Team member user accounts that will also be deleted:`}</p>
                            <ul>
                                {employeeUserAccountsToDelete.map((account) => (
                                    <li key={account.email}>
                                        {account.firstName} {account.lastName} (
                                        {account.email})
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {allPartnersToDelete?.map((account) => (
                        <div key={account.mid}>
                            <p className="font-semibold text-light-secondary">{t`Your company: ${account.name}`}</p>
                            <ul>
                                {account.receiveddocumentsCount > 0 && (
                                    <li>
                                        {t`${account.receiveddocumentsCount} Received Document(s)`}
                                    </li>
                                )}
                                {account.documentCount > 0 && (
                                    <li>
                                        {t`${account.documentCount} Created Document(s)`}
                                    </li>
                                )}
                                {account.activeEmployeeCount > 0 && (
                                    <li>
                                        {t`${account.activeEmployeeCount} Team Member(s)`}
                                    </li>
                                )}
                                {account.activeClientCount > 0 && (
                                    <li>
                                        {t`${account.activeClientCount} Client(s)`}
                                    </li>
                                )}
                            </ul>
                        </div>
                    ))}
                    <FormActions>
                        <Button kind="tertiary" onClick={onCancel}>
                            {t`Cancel`}
                        </Button>
                        <SubmitButton kind="destructive">
                            {t`Confirm Delete`}
                        </SubmitButton>
                    </FormActions>
                </div>
            </form>
        </FormProvider>
    );
};

export default DeletionConfirmation;
